import { colors } from "@elit-hair/elit-tools";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import {
    Box,
    CircularProgress,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemText,
    TextField,
    Typography,
    styled,
} from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import { useFetchCustomData } from "../../content/hooks/useFetchData.hook";

const StyledContainer = styled(Box)({
    width: 300,
    height: "85vh",
    backgroundColor: "#fff",
    borderRight: "1px solid rgba(0, 0, 0, 0.12)",
    overflowY: "hidden",
});

const StyledLink = styled(Link)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    transition: theme.transitions.create(["color"], {
        duration: theme.transitions.duration.short,
    }),
    fontSize: "0.9em",
    overflow: "hidden",
    color: theme.palette.text.primary,
    width: "100%",
    fontWeight: 400,
    "&:hover": {
        color: theme.palette.primary.main,
    },
}));

const StyledListItem = styled(ListItem)(({ highlight }: { highlight: boolean }) => ({
    cursor: "pointer",
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
    marginTop: 5,
    backgroundColor: highlight ? colors.elitInfoBlue : colors.elitButtonDisabledLight,
    color: highlight ? "#fff" : "inherit",
    borderRadius: "0.5rem",
}));

const SearchBox = styled(Box)(({ theme }) => ({
    width: "100%",
    display: "flex",
    alignItems: "center",
    position: "relative",
    marginTop: theme.spacing(3),
}));

const MenuContainer = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(4),
    width: "100%",
    height: "100%",
    padding: theme.spacing(3),
    overflow: "auto",
}));

const LoadingContainer = styled(Box)({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100vh",
});

const NotFoundItem = ({ message = "Not Found" }) => (
    <ListItem sx={{ color: "text.disabled", mt: 1, pl: 0 }}>{message}</ListItem>
);

const SearchField = ({ value, onChange, onClear }) => (
    <TextField
        value={value}
        onChange={onChange}
        placeholder="Search"
        InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1 }} fontSize="small" />,
            endAdornment: (
                <IconButton
                    title="Clear"
                    aria-label="Clear"
                    size="small"
                    style={{ visibility: value ? "visible" : "hidden" }}
                    onClick={onClear}
                >
                    <ClearIcon fontSize="small" />
                </IconButton>
            ),
        }}
        sx={{ flex: 1 }}
    />
);

const ItemsList = ({ title, items, currentPath, emptyMessage = "No data available" }) => (
    <List>
        <Typography component="h2" fontWeight="bold">
            {title}
        </Typography>
        {items && items.length > 0 ? (
            items.map((item) => (
                <StyledListItem key={item.id || item.url} highlight={currentPath === `/dashboard/calc/${item.id}`}>
                    <StyledLink title={item.name || item.url} to={`/dashboard/calc/${item.id}`}>
                        <ListItemText primary={item.name || item.url} />
                    </StyledLink>
                </StyledListItem>
            ))
        ) : (
            <NotFoundItem message={emptyMessage} />
        )}
    </List>
);

function Sidebar() {
    const location = useLocation();
    const [searchContent, setSearchContent] = useState<string>("");
    const { customData } = useFetchCustomData();

    const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchContent(e.target.value);
    }, []);

    const handleClearSearch = useCallback(() => {
        setSearchContent("");
    }, []);

    const filteredData = useMemo(() => {
        if (!customData?.data?.list) return null;

        return searchContent
            ? customData.data.list.filter(
                  (item: any) =>
                      item.url?.toLowerCase().includes(searchContent.toLowerCase()) ||
                      item.name?.toLowerCase().includes(searchContent.toLowerCase())
              )
            : customData.data.list;
    }, [searchContent, customData?.data?.list]);

    const { activeItems, inactiveItems } = useMemo(() => {
        if (!filteredData) return { activeItems: [], inactiveItems: [] };

        return {
            activeItems: filteredData.filter((item: any) => item.activated === true),
            inactiveItems: filteredData.filter((item: any) => item.activated === false),
        };
    }, [filteredData]);

    if (!customData || !filteredData) {
        return (
            <LoadingContainer>
                <CircularProgress />
                <Typography>Loading...</Typography>
            </LoadingContainer>
        );
    }

    if (customData.error) {
        return <Typography>Error loading data: {customData.error}</Typography>;
    }

    return (
        <StyledContainer>
            <MenuContainer>
                <SearchBox>
                    <SearchField value={searchContent} onChange={handleSearchChange} onClear={handleClearSearch} />
                </SearchBox>
                <Box>
                    <ItemsList
                        title="Active"
                        items={activeItems}
                        currentPath={location.pathname}
                        emptyMessage={searchContent ? "Not Found" : "No data available"}
                    />
                    <Divider />
                    <ItemsList
                        title="Inactive"
                        items={inactiveItems}
                        currentPath={location.pathname}
                        emptyMessage={searchContent ? "Not Found" : "No data available"}
                    />
                </Box>
            </MenuContainer>
        </StyledContainer>
    );
}

export default Sidebar;
